'use client'

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import { cx } from '@/lib/utils'

interface CarouselProps {
  children: ReactNode[]
  /** CSS width for items, e.g. "75vw", "100%". Defaults to "100%" */
  suggestedWidth?: string
  /** Minimum item width in px */
  minWidth?: number
  /** Maximum item width in px */
  maxWidth?: number
  /** Gap between items in px */
  gap?: number
  /** Additional className for the scroll container */
  className?: string
  /** Background color for arrow buttons */
  arrowBg?: string
  /**
   * When set, items snap to exactly this many columns on large (`desktop`, 1400px+)
   * screens, overriding suggestedWidth/min/max there. If all items fit, the nav
   * (dots + arrows) auto-hides.
   */
  desktopColumns?: number
  /** On mobile (<md), extend the right edge to the viewport edge (full-bleed scroll). */
  bleedRightMobile?: boolean
  /**
   * On desktop (md+), extend the right edge to the viewport edge. Assumes the
   * standard section side padding (48px, 96px at desktop-xl) — true for the home
   * sections that use this.
   */
  bleedRightDesktop?: boolean
  /**
   * Nav layout. `split` (default): dots on the left, arrows on the right at md+
   * (arrows move left, dots hide, on mobile). `left`: arrows always on the left,
   * dots always hidden.
   */
  navAlign?: 'split' | 'left'
  /** Override the nav row's top margin (default `mt-7`). */
  navClassName?: string
}

const ArrowLeft = () => (
  <svg width="20" height="20" viewBox="0 0 20 21" fill="none">
    <path d="M16.667 10.893H3.334" stroke="currentColor" strokeWidth="1.245" strokeLinejoin="round" />
    <path d="M10 17.533 3.333 10.893 10 4.254" stroke="currentColor" strokeWidth="1.245" />
  </svg>
)

const ArrowRight = () => (
  <svg width="20" height="20" viewBox="0 0 20 21" fill="none">
    <path d="M3.333 10.893h13.334" stroke="currentColor" strokeWidth="1.245" strokeLinejoin="round" />
    <path d="M10 4.254l6.667 6.639L10 17.533" stroke="currentColor" strokeWidth="1.245" />
  </svg>
)

export const Carousel = ({
  children,
  suggestedWidth = '100%',
  minWidth,
  maxWidth,
  gap = 16,
  className,
  arrowBg = 'bg-transparent',
  desktopColumns,
  bleedRightMobile = false,
  bleedRightDesktop = false,
  navAlign = 'split',
  navClassName = 'mt-7',
}: CarouselProps) => {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [pageCount, setPageCount] = useState(1)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const scrollLeft = el.scrollLeft
    const maxScroll = el.scrollWidth - el.clientWidth

    setCanScrollLeft(scrollLeft > 1)
    setCanScrollRight(maxScroll > 1 && scrollLeft < maxScroll - 1)

    // Pages: at least 2 when there's overflow, +1 for each additional viewport of scroll.
    // Require a real clientWidth: when the carousel is measured in a zero-width
    // container (hidden/collapsed ancestor, or a transient pre-layout pass),
    // scrollWidth / 0 is Infinity → pageCount Infinity → Array.from throws
    // RangeError. Treat that as "no pagination".
    if (maxScroll > 1 && el.clientWidth > 0) {
      const pages = Math.max(2, Math.round(el.scrollWidth / el.clientWidth))
      setPageCount(pages)
      const currentPage = Math.round((scrollLeft / maxScroll) * (pages - 1))
      setActiveIndex(Math.min(currentPage, pages - 1))
    } else {
      setPageCount(1)
      setActiveIndex(0)
    }
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    el.addEventListener('scroll', updateState, { passive: true })
    window.addEventListener('resize', updateState)

    // ResizeObserver for container size changes
    const ro = new ResizeObserver(updateState)
    ro.observe(el)

    // MutationObserver for children changes
    const mo = new MutationObserver(updateState)
    mo.observe(el, { childList: true, subtree: true })

    return () => {
      el.removeEventListener('scroll', updateState)
      window.removeEventListener('resize', updateState)
      ro.disconnect()
      mo.disconnect()
    }
  }, [updateState])

  const scrollToPage = useCallback(
    (page: number) => {
      const el = scrollRef.current
      if (!el) return
      const maxScroll = el.scrollWidth - el.clientWidth
      if (maxScroll <= 0 || pageCount <= 1) return
      el.scrollTo({ left: (page / (pageCount - 1)) * maxScroll, behavior: 'smooth' })
    },
    [pageCount]
  )

  const navigate = (dir: 'left' | 'right') => {
    const next = dir === 'left' ? Math.max(0, activeIndex - 1) : Math.min(pageCount - 1, activeIndex + 1)
    scrollToPage(next)
  }

  // Build item style: width = suggestedWidth, clamped by min/max
  // max-width also capped at 100% (viewport of scroll container)
  // +1px buffer so 4 fractional column widths never round up past the container
  // (which would falsely trip canScrollRight and show the nav when all items fit).
  const desktopItemWidth =
    desktopColumns != null ? `calc((100% - ${(desktopColumns - 1) * gap + 1}px) / ${desktopColumns})` : undefined
  const itemStyle: React.CSSProperties = {
    width: suggestedWidth,
    ...(minWidth != null ? { minWidth: `${minWidth}px` } : {}),
    ...(maxWidth != null ? { maxWidth: `min(${maxWidth}px, 100%)` } : { maxWidth: '100%' }),
    flexShrink: 0,
    ...(desktopItemWidth ? ({ '--carousel-desktop-w': desktopItemWidth } as React.CSSProperties) : {}),
  }

  const showNav = pageCount > 1 || canScrollRight

  return (
    <div
      className={cx(
        'w-full min-w-0 overflow-hidden',
        bleedRightMobile && 'max-xs:w-[calc(50vw_+_50%)]',
        // When desktopColumns makes all items fit at `desktop`, drop the right bleed there so the
        // grid fits the container instead of running to the viewport edge; keep the bleed for the
        // md→desktop scrolling range below it.
        bleedRightDesktop &&
          (desktopColumns != null
            ? 'md:w-[calc(100%_+_48px)] desktop:w-full'
            : 'md:w-[calc(100%_+_48px)] desktop-xl:w-[calc(100%_+_96px)]')
      )}
    >
      <div
        ref={scrollRef}
        className={cx('no-scrollbar flex w-full snap-x snap-mandatory overflow-x-auto', className)}
        style={{ gap: `${gap}px` }}
      >
        {children.map((child, i) => (
          <div
            key={i}
            className={cx(
              'snap-start',
              desktopColumns != null && 'desktop:w-[var(--carousel-desktop-w)]! desktop:min-w-0! desktop:max-w-none!'
            )}
            style={itemStyle}
          >
            {child}
          </div>
        ))}
      </div>

      {showNav && (
        <div
          className={cx(
            navClassName,
            'flex items-center gap-3',
            // Cards bleed to the edge, but keep the nav within the section gutter.
            bleedRightDesktop && 'md:mr-[48px] desktop-xl:mr-[96px]'
          )}
        >
          {/* Dots — hidden on mobile (arrows move to the left there); always hidden when navAlign=left */}
          <div
            className={cx(
              'flex min-w-0 flex-1 items-center gap-[6px]',
              navAlign === 'left' ? 'hidden' : 'max-xs:hidden'
            )}
          >
            {Array.from({ length: pageCount }, (_, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                aria-label={t('client.carousel.goToSlide', 'Go to slide {{n}}', { n: i + 1 })}
                aria-current={i === activeIndex ? 'true' : undefined}
                onClick={() => scrollToPage(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') scrollToPage(i)
                }}
                className={cx(
                  'h-[6px] min-w-0 max-w-[51px] flex-1 cursor-pointer rounded-full',
                  i === activeIndex ? 'bg-navy/80' : 'bg-navy/20'
                )}
              />
            ))}
          </div>

          {/* Arrows — left-aligned on mobile (no dots); navAlign=split moves them right at md+ */}
          <div className={cx('flex shrink-0 items-center gap-3', navAlign === 'split' && 'md:ml-auto')}>
            <button
              type="button"
              data-tracking="ignore"
              onClick={() => navigate('left')}
              disabled={!canScrollLeft}
              className={cx(
                'flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-full border border-navy p-[13px]',
                arrowBg,
                !canScrollLeft && 'cursor-default opacity-20'
              )}
              aria-label={t('client.carousel.previous', 'Previous')}
            >
              <ArrowLeft />
            </button>
            <button
              type="button"
              data-tracking="ignore"
              onClick={() => navigate('right')}
              disabled={!canScrollRight}
              className={cx(
                'flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-full border border-navy p-[13px]',
                arrowBg,
                !canScrollRight && 'cursor-default opacity-20'
              )}
              aria-label={t('client.carousel.next', 'Next')}
            >
              <ArrowRight />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
